// Brand-neutral lock chooser: a few taps about the door, then the locks that fit it. Shared
// by /shop/find-my-lock, the shop home promo and the brand hubs (which filter the picks to
// their brand). The picks are not hand-written lists: every step filters the catalogue
// through lib/lockFit, so a lock can only ever land in the section its own data says it
// fits. lockChooser.test.js audits every path and snapshots the whole table.
//
// The walk (Hugh, 17 Sep 2026: three taps at most; 19–20 Sep research round):
//   slides or swings → (a ranchslider is done here: every sliding lock fits every slider)
//   → timber, aluminium or not sure → what hardware is on the door now → the picks.
// Nothing about shelter, swing direction or the backset: the weather rating is a chip on the
// card, and the size is never asked of anyone who came this way: the locksmith takes it from
// the door photos after payment (Hugh, 22 Sep 2026). The hardware question only finds the
// lock that fits with the easiest install — it never moves the fitted price.
//
// Material filters only what a product's own data excludes (Schlage Omnia is timber only)
// and otherwise orders: narrow-stile locks first on aluminium, the 60/70 mm locks first on
// timber. The hardware cards are ordered by material too — the deadlatch first on a timber
// door, the lever-and-mortice first on aluminium.
//
// answers = { slides, material?, onDoor?, entry? }
//   entry — an optional results filter ('fingerprint' | 'phone'), not a step.
const { products } = require('../content/products');
const fitLib = require('./lockFit');

// Options are [value, label, hint?]. Labels are written from inside the door, in the words
// people use in Auckland (ranchslider, deadlock, "the Lockwood"), never trade terms.
const STEPS = {
  slides: {
    q: 'Does the door slide or swing?',
    help: 'French doors and bifolds count as swinging.',
    options: [
      ['slides', 'Slides open (ranchslider)'],
      ['swings', 'Swings open on hinges'],
    ],
  },
  material: {
    q: 'What is the door made of?',
    help: 'Aluminium doors have a thin metal frame, usually around glass. Timber doors are solid wood, painted or stained.',
    options: [
      ['aluminium', 'Aluminium'],
      ['timber', 'Timber'],
      ['unsure', 'Not sure, or something else', 'uPVC, steel, composite — we check it from your photos'],
    ],
  },
  onDoor: {
    q: 'What is on the door now?',
    options: [
      ['mortice', 'A lever or handle with a keyhole below it — the lock sits in the door edge', 'The usual aluminium door lock, or a timber mortice lock'],
      ['multipoint', 'A handle you lift up to lock — it locks top and bottom', 'Lift-to-lock, multipoint'],
      ['deadbolt', 'A round deadlock or deadbolt — thumb turn inside, key outside'],
      ['lever', 'A knob or lever that locks with a key — the handle is the lock'],
      ['nightlatch', 'A Lockwood 001 or Yale-type deadlatch — box on the inside, key outside'],
      ['gripset', 'A long pull handle with a deadlock above it (Gainsborough-type gripset)'],
      ['none', 'Nothing yet — a blank door'],
      ['other', 'Not sure, or something else', 'More than one lock? Pick the one you lock up with.'],
    ],
  },
};

// The hardware cards in the order each kind of door usually has them (§6 of the 19 Sep
// research; Hugh's job history to confirm).
const ON_DOOR_ORDER = {
  aluminium: ['mortice', 'multipoint', 'deadbolt', 'lever', 'nightlatch', 'gripset', 'none', 'other'],
  timber: ['nightlatch', 'deadbolt', 'lever', 'gripset', 'mortice', 'multipoint', 'none', 'other'],
  unsure: ['mortice', 'deadbolt', 'lever', 'nightlatch', 'multipoint', 'gripset', 'none', 'other'],
};

// A step's options in the order this door shows them (all options; brand filtering is optionsFor).
function optionsOf(stepKey, a) {
  const opts = STEPS[stepKey].options;
  if (stepKey !== 'onDoor') return opts;
  const order = ON_DOOR_ORDER[(a && a.material) || 'unsure'] || ON_DOOR_ORDER.unsure;
  return opts.slice().sort((x, y) => order.indexOf(x[0]) - order.indexOf(y[0]));
}

const CONFIRMED_SUPPLIERS = ['elements', 'konec'];
const supplierOf = (p) => (p.variants && p.variants[0] ? p.variants[0].supplier : '');

// "Our pick" order, all from data: the four-point answers first on a lift-to-lock door, the
// lock made for this stile first, sheltered-only locks after the weather-rated ones (unless the
// customer asked for a fingerprint), then Hugh's chooserRank, confirmed suppliers, price.
function rank(p, a) {
  let stile = 0;
  if (fitLib.isMortice(p) || fitLib.isRetrofit(p)) {
    if (a.material === 'aluminium') stile = fitLib.isNarrowStile(p) ? 0 : 1;
    else stile = fitLib.isStandardOnly(p) ? 0 : 1;
  }
  let mp = 1;
  if (a.onDoor === 'multipoint') mp = fitLib.multipoint(p) === 'provides' ? 0 : fitLib.multipoint(p) === 'keeps' ? 0.5 : 1;
  const shelter = a.entry !== 'fingerprint' && fitLib.isShelteredOnly(p) ? 1 : 0;
  const pref = p.chooserRank || 9;
  const supplier = CONFIRMED_SUPPLIERS.indexOf(supplierOf(p)) === -1 ? 1 : 0;
  return [mp, stile, shelter, pref, supplier, p.priceIncGst || 0];
}
function byRank(a) {
  return (p, q) => {
    const rp = rank(p, a); const rq = rank(q, a);
    for (let i = 0; i < rp.length; i++) if (rp[i] !== rq[i]) return rp[i] - rq[i];
    return 0;
  };
}

// Every lock the chooser may ever recommend: a digital door lock we can sell, whose fit is known.
function universe() {
  return products.filter((p) => fitLib.isDigitalLock(p) && fitLib.isOrderable(p)
    && (fitLib.isSliding(p) || fitLib.isBoreSwap(p) || fitLib.isRetrofit(p) || fitLib.isRim(p) || (fitLib.isMortice(p) && fitLib.backsetKnown(p))));
}

// Does this lock fit the door the answers describe? (Only the answers given so far apply.)
function fitsDoor(p, a) {
  if (a.slides === 'slides') return fitLib.isSliding(p);
  if (a.slides !== 'swings') return false;
  if (fitLib.isSliding(p)) return false;
  if (a.material && !fitLib.fitsMaterial(p, a.material)) return false;
  if (a.onDoor && !fitLib.fitsOnDoor(p, a.onDoor)) return false;
  return true;
}
// 'phone' means Apple Home / Google Home / Alexa; when no such lock fits the door, the locks
// with their own app stand in (remote unlock via the gateway), and the note says so.
function applyEntry(list, a) {
  if (a.entry === 'fingerprint') return { list: list.filter(fitLib.hasFingerprint), fallback: false };
  if (a.entry === 'phone') {
    const eco = list.filter(fitLib.hasEcosystem);
    return eco.length ? { list: eco, fallback: false } : { list: list.filter(fitLib.hasApp), fallback: true };
  }
  return { list: list, fallback: false };
}
// The locks still in play after the answers so far, best first.
function candidates(a) {
  const list = universe().filter((p) => fitsDoor(p, a));
  const e = applyEntry(list, a);
  return { list: e.list.sort(byRank(a)), fallback: e.fallback };
}

// Which steps to show, in order, given the answers so far. A ranchslider is done after the
// first tap: material and hardware change nothing about which sliding lock fits.
function stepOrder(a) {
  if (!a.slides || a.slides === 'slides') return ['slides'];
  return ['slides', 'material', 'onDoor'];
}

// The answer key a step writes (every step writes its own key; kept for the wizard).
function keyOf(stepKey) {
  return stepKey;
}

// Done when every step the answers call for has an answer. "Something else" ends the walk on
// its own: no software pick, photos instead.
function isDone(a) {
  const order = stepOrder(a);
  return order.every((k) => a[keyOf(k)] !== undefined);
}

const ON_DOOR_WORDS = {
  mortice: 'lever and mortice lock',
  multipoint: 'lift-to-lock handle',
  deadbolt: 'round deadlock',
  lever: 'keyed knob or lever',
  nightlatch: 'Lockwood-type deadlatch',
  gripset: 'entrance gripset',
  none: 'no lock yet',
  other: 'something unusual, or more than one lock',
};

// One line that plays the customer's answers back, shown above the picks.
function describe(a) {
  if (a.slides === 'slides') return 'Sliding door (ranchslider).';
  const parts = [];
  const material = a.material === 'timber' ? 'timber' : a.material === 'aluminium' ? 'aluminium' : '';
  parts.push((material ? material + ' ' : '') + 'hinged door');
  if (a.onDoor && ON_DOOR_WORDS[a.onDoor]) parts.push(ON_DOOR_WORDS[a.onDoor]);
  const text = parts.join(', ').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) + '.' : '';
}

const OTHER_NOTE = 'A digital lock replaces the main lock. Any extra deadbolt or deadlatch is disabled at install — unlocked, inside turn off — so it can never hold the door shut against a code, and nothing needs patching. Which lock suits depends on the main lock and the door, so this one is a photo job: a locksmith looks and picks it, no visit needed.';

// Returns { slugs: [...every lock that fits, best first], note: string|null }.
// The notes never ask for a photo before a sale (that ask comes after payment) and never
// mention the backset or a mortice size: nobody who comes this way is asked for one.
function recommend(a) {
  if (!isDone(a)) return { slugs: [], note: null };
  if (a.onDoor === 'other') return { slugs: [], note: OTHER_NOTE };
  const c = candidates(a);
  const slugs = c.list.map((p) => p.slug);
  const notes = [];
  if (c.fallback) notes.push('No Apple Home, Google Home or Alexa lock fits this door yet; these give you app control from the lock’s own app, with remote unlock via the Wi-Fi gateway.');
  if (a.slides === 'slides') notes.push('Every sliding lock comes with the sliding mortice it needs.');
  if (a.onDoor === 'deadbolt') notes.push('Each of these swaps straight onto the round bore that is already there, no cutting.');
  if (a.onDoor === 'lever') notes.push('Each of these replaces the keyed knob or lever like for like, on the same bore.');
  if (a.onDoor === 'gripset') notes.push('The deadlock above a gripset sits in a standard round bore, so these swap in; with installation a cover plate for the pull-handle holes is included.');
  if (a.onDoor === 'nightlatch') notes.push('A deadlatch has no bore behind it. With installation we bore the door and the edge and fit a jamb strike, all in the fitted price; fitting a deadbolt yourself needs a 54 mm hole saw and a new edge hole.' + (slugs.indexOf('schlage-s480-rim-lock') !== -1 ? ' The S-480 mounts on the inside face like the deadlatch it replaces.' : ''));
  if (a.onDoor === 'mortice' && slugs.indexOf('aqara-u200') !== -1) notes.push('The U200 fits over the cylinder if your mortice lock has one; the others replace the lock as a set.');
  if (a.onDoor === 'multipoint') notes.push('Unity Slim replaces a lift-to-lock with a four-point lock of its own and the U200 keeps yours; every other lock here fits a single-point mortice instead.');
  if (a.onDoor === 'none') notes.push('Blank door: with installation we cut the door for the lock on the day.');
  return { slugs, note: notes.length ? notes.join(' ') : null };
}

// ── Presentation: the pick, a spread of alternates, and the chips on every card ──

// Two-word reasons a lock is on the list, from its data. At most three per card.
function reasonsFor(p) {
  const out = [];
  if (fitLib.isSliding(p)) out.push('Ranchslider');
  else if (fitLib.isRetrofit(p)) out.push('Keeps your lock');
  else if (fitLib.isDeadbolt(p)) out.push('Straight swap');
  else if (fitLib.isLeverSet(p)) out.push('Lever set');
  else if (fitLib.isRim(p)) out.push('No new holes');
  else if (fitLib.isNarrowStile(p)) out.push('Narrow stile');
  else if (fitLib.isStandardOnly(p)) out.push('Timber mortice');
  if (fitLib.hasFingerprint(p)) out.push('Fingerprint');
  if (fitLib.hasApple(p)) out.push('Apple Home');
  else if (fitLib.hasEcosystem(p)) out.push('Google / Alexa');
  if (fitLib.hasWifi(p)) out.push('Wi-Fi built in');
  if (fitLib.isMechanical(p)) out.push('No batteries');
  if ((p.saleStatus || 'sale') === 'install-only') out.push('Fitted only');
  return out.slice(0, 3);
}
// The things a buyer regrets not hearing, as two or three words on the card.
function warningsFor(p, a) {
  const out = [];
  if (fitLib.isShelteredOnly(p)) out.push('Covered entry only');
  if (a.onDoor === 'nightlatch' && fitLib.isBoreSwap(p)) out.push('Needs new holes');
  if (a.material === 'aluminium' && (fitLib.isBoreSwap(p) || fitLib.isRim(p) || fitLib.isRetrofit(p))) out.push('Wide stile needed');
  if (a.onDoor === 'multipoint' && fitLib.isMortice(p) && fitLib.multipoint(p) !== 'provides') out.push('Single-point lock');
  return out;
}
function chipsFor(p, a) {
  return { reasons: reasonsFor(p), warnings: warningsFor(p, a || {}) };
}

// One line of reasoning under the pick, built from the same facts as the chips.
function whyFor(p, a) {
  const bits = [];
  if (fitLib.isSliding(p)) bits.push('built for ranchslider hook locks');
  else if (fitLib.isRetrofit(p)) bits.push('fits over your existing cylinder, no new holes');
  else if (fitLib.isRim(p)) bits.push('mounts on the inside face like the deadlatch it replaces');
  else if (fitLib.isDeadbolt(p) && a.onDoor === 'nightlatch') bits.push('a proper deadbolt in place of the deadlatch, with the new holes bored as part of the fitted price');
  else if (fitLib.isDeadbolt(p)) bits.push('drops into the round bore that is already there');
  else if (fitLib.isLeverSet(p)) bits.push('replaces the keyed lever like for like');
  else if (a.onDoor === 'multipoint' && fitLib.multipoint(p) === 'provides') bits.push('a four-point lock like the one it replaces');
  else if (a.material === 'aluminium' && fitLib.isNarrowStile(p)) bits.push('made for narrow aluminium stiles');
  else if (fitLib.isStandardOnly(p)) bits.push('made for the standard timber mortice');
  else bits.push('cut to fit your door');
  const ip = (((p.fit && p.fit.weatherRating) || '').match(/IP\s?\d\d|IPX\d/i) || [])[0];
  if (ip) bits.push(ip.replace(/\s/g, '') + ' outside');
  else if (fitLib.isShelteredOnly(p)) bits.push('for a covered entry');
  if (p.chooserRank === 1) bits.push('the lock we fit most');
  return 'Why this one: ' + bits.join(', ') + '.';
}

const VISIBLE = 5;
const twinKey = (p) => p.brand + '|' + ((p.fit && p.fit.lockType) || '');

// The pick plus up to four alternates chosen for spread: the cheapest that fits, a fingerprint
// option, an Apple Home / Google / Alexa option, on a deadlatch door the rim lock that needs no
// new holes, then the next-best from another brand or with Wi-Fi built in. Near-twins (905 and
// 905W) never both show unless a filter asks for them.
function chooseVisible(list, a) {
  if (!list.length) return [];
  const out = [];
  const twins = {};
  const add = (p) => {
    if (!p || out.indexOf(p) !== -1 || out.length >= VISIBLE) return;
    const k = twinKey(p);
    if (twins[k]) return;
    twins[k] = true;
    out.push(p);
  };
  const pick = list[0];
  add(pick);
  add(list.slice().sort((x, y) => (x.priceIncGst || 0) - (y.priceIncGst || 0))[0]);
  add(list.find(fitLib.hasFingerprint));
  add(list.find(fitLib.hasEcosystem));
  if (a && a.onDoor === 'nightlatch') add(list.find(fitLib.isRim));
  add(list.find((p) => p.brand !== pick.brand) || list.find(fitLib.hasWifi));
  for (let i = 0; i < list.length && out.length < VISIBLE; i++) add(list[i]);
  // The pick stays first; the alternates follow in rank order.
  return [pick].concat(out.filter((p) => p !== pick).sort((x, y) => list.indexOf(x) - list.indexOf(y)));
}

const PROMISE = 'Fit checked from your door photos before we book. If a different lock suits, you pay or get back the difference.';

// Everything the results screen needs for a finished walk:
// { slugs, pick, visible, rest, why, chips: {slug: {reasons, warnings}}, note, promise }
function present(a) {
  const rec = recommend(a);
  const list = rec.slugs.map((slug) => products.find((p) => p.slug === slug)).filter(Boolean);
  const visible = chooseVisible(list, a);
  const chips = {};
  list.forEach((p) => { chips[p.slug] = chipsFor(p, a); });
  return {
    slugs: rec.slugs,
    pick: list[0] || null,
    visible: visible.map((p) => p.slug),
    rest: list.filter((p) => visible.indexOf(p) === -1).map((p) => p.slug),
    why: list[0] ? whyFor(list[0], a) : null,
    chips,
    note: rec.note,
    promise: list.length ? PROMISE : null,
  };
}

// The install questions a chooser walk already answers, for the lock the customer picked
// from it (Hugh, 18 Sep 2026: having gone through the tree, nobody should type the door in
// again). Material carries straight over (a ranchslider is aluminium); "what is on the door"
// becomes same-type / different (+ which) by asking lockFit whether this lock replaces that
// hardware like for like. Returns {} for an incomplete walk or the "something else" path.
const ON_DOOR_TO_TYPE = { deadbolt: 'tubular-deadbolt', lever: 'deadlatch', gripset: 'gripset', nightlatch: 'rim', mortice: 'mortice', multipoint: 'multipoint' };
function installPrefill(product, a) {
  if (!product || !a || !isDone(a) || a.onDoor === 'other') return {};
  const out = {};
  if (a.slides === 'slides') { out.doorMaterial = 'aluminium'; return out; }
  if (a.material === 'timber' || a.material === 'aluminium') out.doorMaterial = a.material;
  if (a.onDoor === 'none') { out.existingLock = 'none'; return out; }
  const same = (a.onDoor === 'deadbolt' && fitLib.isDeadbolt(product))
    || (a.onDoor === 'lever' && fitLib.isLeverSet(product))
    || (a.onDoor === 'nightlatch' && fitLib.isRim(product))
    || (a.onDoor === 'mortice' && fitLib.isMortice(product))
    || (a.onDoor === 'multipoint' && (fitLib.isRetrofit(product) || fitLib.multipoint(product) === 'provides'));
  if (same) { out.existingLock = 'same-type'; return out; }
  out.existingLock = 'different';
  out.existingLockType = ON_DOOR_TO_TYPE[a.onDoor] || 'other';
  return out;
}

// Every completed answer set reachable from `answers` (a ranchslider plus 3 × 8 hinged doors).
function completions(answers) {
  const out = [];
  const walk = (a) => {
    if (isDone(a)) { out.push(a); return; }
    const order = stepOrder(a);
    let next = null;
    for (let i = 0; i < order.length; i++) if (a[keyOf(order[i])] === undefined) { next = order[i]; break; }
    if (!next) return;
    const opts = optionsOf(next, a);
    for (let i = 0; i < opts.length; i++) {
      const b = Object.assign({}, a); b[keyOf(next)] = opts[i][0];
      walk(b);
    }
  };
  walk(answers || {});
  return out;
}

// Options for a step that can still end in a pick of `brand` (all options when no brand), in
// this door's order. brandOf(slug) → brand name; injected so the component decides how to look
// brands up.
function optionsFor(stepKey, answers, brand, brandOf) {
  const opts = optionsOf(stepKey, answers);
  if (!brand) return opts;
  return opts.filter((opt) => {
    const a = Object.assign({}, answers); a[keyOf(stepKey)] = opt[0];
    return completions(a).some((done) => recommend(done).slugs.some((slug) => brandOf(slug) === brand));
  });
}

module.exports = { STEPS, ON_DOOR_ORDER, optionsOf, stepOrder, isDone, recommend, present, chipsFor, whyFor, describe, completions, optionsFor, keyOf, universe, candidates, fitsDoor, installPrefill, VISIBLE, PROMISE };
